import { z } from 'zod'
import { useZorm } from 'react-zorm'
import {
    FormField,
    fieldDescription,
    withProps,
} from '@codecorp/zorm-conventions'
import { ShowBarcode } from '../components/show-barcode'
import { CreatorButtons } from '../components/creator-buttons'
import VCard from 'vcard-creator'
import { useFormReset } from '../hooks/use-form'
import { useShowModal } from '../hooks/use-show-modal'

const FormSchema = z.object({
    label: z.string().describe(
        fieldDescription({
            placeHolder: 'This name will be placed under the QR Code.',
        })
    ),
    firstName: z.string().describe(
        fieldDescription({
            autoComplete: 'given-name',
            placeHolder: 'Enter First Name',
        })
    ),
    lastName: z.string().describe(
        fieldDescription({
            autoComplete: 'family-name',
            placeHolder: 'Enter Last Name',
        })
    ),
    mobileNumber: z
        .string()
        .optional()
        .describe(fieldDescription({ inputType: 'tel' })),
    workNumber: z
        .string()
        .optional()
        .describe(fieldDescription({ inputType: 'tel' })),
    email: z
        .string()
        .optional()
        .describe(
            fieldDescription({
                autoComplete: 'email',
                placeHolder: 'Enter Email',
            })
        ),
    company: z
        .string()
        .optional()
        .describe(fieldDescription({ placeHolder: 'Enter Company Name' })),
    jobTitle: z
        .string()
        .optional()
        .describe(fieldDescription({ placeHolder: 'Enter Job Title' })),
    street: z
        .string()
        .optional()
        .describe(fieldDescription({ placeHolder: 'Enter Street Name' })),
    city: z
        .string()
        .optional()
        .describe(fieldDescription({ placeHolder: 'Enter City' })),
    region: z
        .string()
        .optional()
        .describe(fieldDescription({ placeHolder: 'Enter Region' })),
    zipCode: z
        .string()
        .optional()
        .describe(fieldDescription({ placeHolder: 'Enter ZIP' })),
    website: z
        .string()
        .optional()
        .describe(fieldDescription({ placeHolder: 'Enter Website' })),
})

const vcard = ({
    firstName,
    lastName,
    mobileNumber,
    workNumber,
    email,
    company,
    jobTitle,
    street,
    city,
    region,
    zipCode,
    website,
}: z.infer<typeof FormSchema>) => {
    const vcard = new VCard()
    vcard.addName(lastName, firstName)
    if (mobileNumber) vcard.addPhoneNumber(mobileNumber, 'CELL')
    if (workNumber) vcard.addPhoneNumber(workNumber, 'WORK')
    if (email) vcard.addEmail(email)
    if (company) vcard.addCompany(company)
    if (jobTitle) vcard.addJobtitle(jobTitle)
    if (street || city || region || zipCode)
        vcard.addAddress('', '', street, city, region, zipCode, '')
    if (website) vcard.addURL(website)
    return vcard.toString()
}

export const VcardBarcodeCreator = () => {
    const { setSubmittedBarcode, handleFormReset } = useFormReset(undefined)
    const showModal = useShowModal()

    const zo = useZorm('vcardBarcode', FormSchema, {
        onValidSubmit: async ({ preventDefault, data }) => {
            preventDefault()

            setSubmittedBarcode(data)
            showModal(
                <ShowBarcode
                    label={data.label}
                    barcode={vcard(data)}
                />
            )
        },
    })

    return (
        <div>
            <form
                ref={zo.ref}
                className="ml-10 mr-10 leading-10"
                onReset={handleFormReset}
            >
                {zo.fields.label(FormField)}
                {zo.fields.firstName(FormField)}
                {zo.fields.lastName(FormField)}
                {zo.fields.mobileNumber(
                    withProps({ defaultValue: '+1' }, FormField)
                )}
                {zo.fields.workNumber(
                    withProps({ defaultValue: '+1' }, FormField)
                )}
                {zo.fields.email(FormField)}
                {zo.fields.company(FormField)}
                {zo.fields.jobTitle(FormField)}
                {zo.fields.street(FormField)}
                {zo.fields.city(FormField)}
                {zo.fields.region(FormField)}
                {zo.fields.zipCode(FormField)}
                {zo.fields.website(FormField)}
                <CreatorButtons />
            </form>
        </div>
    )
}
